<script lang="ts">import { cfgBrandTitle, cfgHasSports, stateAffiliatesEnabled, cfgHelpcenterUrl, cfgToThemeCdnPath, FeatureSet, ReleaseStage, stateFeatureSet, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isFullScreenGame, loggedIn, stateShowSeal, stateShowSealText, toSiteUrl } from "@infinitro/global";
import CopyrightAlternative4 from "./parts/copyright-alternative4.svelte";
import Seal from "./parts/seal.svelte";
import SocialMedia from "./parts/social-media.svelte";
$: useAlternative4 = $stateUi.footer?.footerStyle === "alternative4";
$: logoPath = cfgToThemeCdnPath(`/misc/logo.svg`);
$: useEmbeddedShellDecor = $stateUi.footer?.useEmbeddedShellDecor ?? false;
$: hideFooterFaqLink = $stateUi.footer?.hideFooterFaqLink ?? false;
$: showFooterSports = cfgHasSports() && ($stateUi.footer?.showSports ?? true);
$: footerTextKey = useEmbeddedShellDecor ? Verbiage.footer_text_flush : Verbiage.footer_text;
$: helpcenterUrl = cfgHelpcenterUrl();
// keyword → path pairs; translated via Verbiage for SEO anchor diversity across locales
const casinoLinks = [
	{
		verbiage: Verbiage.bitcoin_casino,
		path: "/casino/"
	},
	{
		verbiage: Verbiage.bitcoin_slots,
		path: "/slots/"
	},
	{
		verbiage: Verbiage.bitcoin_live_casino,
		path: "/livecasino/"
	},
	{
		verbiage: Verbiage.bitcoin_blackjack,
		path: "/livecasino/"
	},
	{
		verbiage: Verbiage.bitcoin_roulette,
		path: "/livecasino/"
	},
	{
		verbiage: Verbiage.bitcoin_baccarat,
		path: "/livecasino/"
	},
	{
		verbiage: Verbiage.bonus_buy_slots,
		path: "/featurebuyin/"
	},
	{
		verbiage: Verbiage.crypto_game_shows,
		path: "/gameshows/"
	}
];
const sportsLinks = [
	{
		verbiage: Verbiage.bitcoin_sportsbook,
		path: "/sports/"
	},
	{
		verbiage: Verbiage.live_bitcoin_betting,
		path: "/sports/"
	},
	{
		verbiage: Verbiage.bet_on_football,
		path: "/sports/soccer-1/"
	},
	{
		verbiage: Verbiage.bet_on_basketball,
		path: "/sports/basketball-2/"
	},
	{
		verbiage: Verbiage.bet_on_tennis,
		path: "/sports/tennis-5/"
	},
	{
		verbiage: Verbiage.bet_on_ufc,
		path: "/sports/mixed-martial-arts-117/"
	},
	{
		verbiage: Verbiage.bet_on_f1,
		path: "/sports/formula-1-40/"
	},
	{
		verbiage: Verbiage.bet_on_cricket,
		path: "/sports/cricket-21/"
	}
];
const esportsLinks = [
	{
		verbiage: Verbiage.esports_betting,
		path: "/esports/"
	},
	{
		verbiage: Verbiage.bet_on_cs2,
		path: "/sports/counter-strike-109/"
	},
	{
		verbiage: Verbiage.bet_on_dota2,
		path: "/sports/dota-2-111/"
	},
	{
		verbiage: Verbiage.bet_on_lol,
		path: "/sports/league-of-legends-110/"
	},
	{
		verbiage: Verbiage.bet_on_valorant,
		path: "/sports/valorant-194/"
	},
	{
		verbiage: Verbiage.esports_live_betting,
		path: "/esports/"
	}
];
const originalsLinks = [
	{
		verbiage: Verbiage.bitcoin_crash_game,
		path: "/p/flush-originals/crash/"
	},
	{
		verbiage: Verbiage.bitcoin_dice,
		path: "/p/flush-originals/dice/"
	},
	{
		verbiage: Verbiage.bitcoin_mines_game,
		path: "/p/flush-originals/mines/"
	},
	{
		verbiage: Verbiage.crypto_plinko,
		path: "/p/flush-originals/plinko/"
	},
	{
		verbiage: Verbiage.bitcoin_keno,
		path: "/p/flush-originals/keno/"
	},
	{
		verbiage: Verbiage.crypto_wheel,
		path: "/p/flush-originals/wheel/"
	},
	{
		verbiage: Verbiage.crypto_video_poker,
		path: "/p/flush-originals/video_poker/"
	}
];
const promotionsLinks = [
	{
		verbiage: Verbiage.casino_promotions,
		path: "/promotion-page/"
	},
	{
		verbiage: Verbiage.vip_program,
		path: "/vip/"
	},
	{
		verbiage: Verbiage.referral_program,
		path: "/referral/"
	},
	{
		verbiage: Verbiage.flush_races,
		path: "/promotion-page/race/flush-races/"
	},
	{
		verbiage: Verbiage.bonus_terms,
		path: "/bonus-terms/"
	}
];
$: supportLinks = [
	...hideFooterFaqLink ? [] : [{
		verbiage: Verbiage.frequently_asked_questions,
		path: "/faq/"
	}],
	{
		verbiage: Verbiage.provably_fair,
		path: "/fairness/"
	},
	{
		verbiage: Verbiage.responsible_gaming,
		path: "/responsible-gaming/"
	},
	{
		verbiage: Verbiage.fairness_and_rng,
		path: "/fairness/"
	},
	{
		verbiage: Verbiage.terms_of_service,
		path: "/terms-of-service/"
	},
	{
		verbiage: Verbiage.privacy_policy,
		path: "/privacy-policy/"
	},
	{
		verbiage: Verbiage.aml_policy,
		path: "/aml-policy/"
	},
	{
		verbiage: Verbiage.kyc_policy,
		path: "/kyc-policy/"
	},
	...$stateAffiliatesEnabled ? [{
		verbiage: Verbiage.affiliates,
		path: "/affiliate/"
	}] : [],
	{
		verbiage: Verbiage.about_us,
		path: "/about-us/"
	}
];
</script>

{#if !$isFullScreenGame}
	<nav id="footerId" class="footer" class:alternative4-footer={useAlternative4}>
		<div class="footer-container">
			<div class="footer-content">
				<div class="footer-columns-wrapper">
					<!-- Logo column -->
					<div class="footer-column logo-column">
						<a href={toSiteUrl('/', $loggedIn)} class="logo">
							<img src={logoPath} alt={$t(Verbiage.logo_alt, 'alts')} />
						</a>
					</div>

					<!-- SEO keyword columns -->
					<div class="content-columns">
						<div class="footer-column">
							<h4 class="footer-title">{$t(Verbiage.casino)}</h4>
							{#each casinoLinks as link}
								<a href={toSiteUrl(link.path, $loggedIn)} class="footer-link">{$t(link.verbiage)}</a
								>
							{/each}
						</div>

						{#if showFooterSports}
							<div class="footer-column">
								<h4 class="footer-title">{$t(Verbiage.sports)}</h4>
								{#each sportsLinks as link}
									<a href={toSiteUrl(link.path, $loggedIn)} class="footer-link"
										>{$t(link.verbiage)}</a
									>
								{/each}
							</div>
						{/if}

						<div class="footer-column">
							<h4 class="footer-title">{$t(Verbiage.esports)}</h4>
							{#each esportsLinks as link}
								<a href={toSiteUrl(link.path, $loggedIn)} class="footer-link">{$t(link.verbiage)}</a
								>
							{/each}
						</div>

						<div class="footer-column">
							<h4 class="footer-title">{$t(Verbiage.originals)}</h4>
							{#each originalsLinks as link}
								<a href={toSiteUrl(link.path, $loggedIn)} class="footer-link">{$t(link.verbiage)}</a
								>
							{/each}
						</div>

						<div class="footer-column">
							<h4 class="footer-title">{$t(Verbiage.promotions)}</h4>
							{#each promotionsLinks as link}
								<a href={toSiteUrl(link.path, $loggedIn)} class="footer-link">{$t(link.verbiage)}</a
								>
							{/each}
						</div>

						<div class="footer-column">
							<h4 class="footer-title">{$t(Verbiage.support)}</h4>
							<a
								href={helpcenterUrl}
								{...helpcenterUrl.startsWith('/') ? { 'data-sveltekit-reload': true } : {}}
								class="footer-link">{$t(Verbiage.helpdesk)}</a
							>
							{#each supportLinks as link}
								<a href={toSiteUrl(link.path, $loggedIn)} class="footer-link">{$t(link.verbiage)}</a
								>
							{/each}
						</div>
					</div>
				</div>

				{#if !useAlternative4 && $stateShowSealText}
					<div class="separator" />
					<p class="footer-text" class:alternative-footer-text={useAlternative4}>
						{$t(footerTextKey)}
					</p>
				{/if}

				<div class="separator" />

				<div class="footer-bottom">
					<div class="copyright">
						<p class="footer-text">
							&copy;{new Date().getFullYear()}
							{cfgBrandTitle()}
							{$t(Verbiage.all_rights_reserved)}
						</p>
					</div>
					<div class="seal-logos-container">
						{#if $stateShowSeal}
							<Seal />
						{/if}
						{#if $stateFeatureSet === FeatureSet.Preview && wantAtLeastReleaseStage(ReleaseStage.Alpha) && !$stateShowSeal}
							<img
								src={cfgToThemeCdnPath(`/footer/placeholder.svg`)}
								alt={$t(Verbiage.logo_alt, 'alts')}
								class="footer-logo"
							/>
						{/if}
						<a href="https://responsiblegambling.org/" target="_blank" rel="noopener noreferrer">
							<img
								src={cfgToThemeCdnPath(`/footer/responsible-gaming.svg`)}
								alt={$t(Verbiage.logo_alt, 'alts')}
								class="footer-logo"
							/>
						</a>
						<a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer">
							<img
								src={cfgToThemeCdnPath(`/footer/gamcare.svg`)}
								alt={$t(Verbiage.logo_alt, 'alts')}
								class="footer-logo"
							/>
						</a>
						<a href={`${toSiteUrl('/terms-of-service/', $loggedIn)}#point-18`}>
							<img
								src={cfgToThemeCdnPath(`/footer/18+.svg`)}
								alt={$t(Verbiage.logo_alt, 'alts')}
								class="footer-logo"
							/>
						</a>
					</div>
					<div class="social-media">
						<SocialMedia />
					</div>
				</div>
			</div>
		</div>
	</nav>
{/if}

<style>
	.footer {
		z-index: 40;
		width: 100%;
		border-top-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-700);
		--tw-bg-opacity: 1;
		padding-left: 22px;
		padding-right: 22px;
		--tw-backdrop-blur: blur(50px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		display: flex;
		align-items: center;
		justify-content: center;
}

	.alternative4-footer {
		border-width: 0px;
		background-color: var(--component-layout-side-bar-bg);
}

	.footer-container {
		margin-left: auto;
		margin-right: auto;
		margin-bottom: 70px;
		margin-top: 28px;
		display: flex;
		width: 100%;
		max-width: 1136px;
		flex-direction: column;
		gap: 1.25rem;
}

	.footer-content {
		display: flex;
		width: 100%;
		flex-direction: column;
		container-type: inline-size;
}

	.footer-columns-wrapper {
		display: flex;
		flex-wrap: wrap;
		width: 100%;
		gap: 1rem;
	}

	.logo-column {
		max-width: 150px;
		flex: 0 0 auto;
	}

	.content-columns {
		display: grid;
		grid-template-columns: repeat(6, 1fr);
		grid-gap: 1rem;
		flex: 1;
	}

	@container (max-width: 900px) {
		.footer-columns-wrapper {
			flex-direction: column;
		}

		.logo-column {
			width: 100%;
			margin-bottom: 1rem;
		}

		.content-columns {
			grid-template-columns: repeat(3, 1fr);
		}

		.footer-column {
			word-wrap: break-word;
			overflow-wrap: break-word;
			hyphens: auto;
		}

		.footer-link {
			max-width: 100%;
		}
	}

	.footer-column {
		margin-bottom: 1.25rem;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
}

	.logo {
		margin-bottom: 1.25rem;
		margin-right: 1.25rem;
		width: 100px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.footer-title {
		margin-bottom: 0.5rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 700;
		text-transform: uppercase;
		line-height: 14px;
		letter-spacing: 0.1em;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.footer-link {
		margin-bottom: 0.375rem;
		cursor: pointer;
		text-align: start;
		font-size: 0.75rem;
		line-height: 1rem;
		line-height: 16.8px;
		color: var(--color-text-300);
		font-family: var(--font-main);
}

	.footer-link:hover {
		color: var(--color-text-100);
}

	.footer-link:nth-child(n) {
		margin-top: 0.375rem;
}

	@media (min-width: 768px) {
		.footer-link {
				font-size: 0.875rem;
				line-height: 1.25rem;
		}
	}

	.separator {
		margin-top: 0.5rem;
		margin-bottom: 0.5rem;
		height: 1px;
		width: 100%;
		background-color: var(--color-neutral-900);
}

	.footer-text {
		margin-top: 0.5rem;
		margin-bottom: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-300);
		font-family: var(--font-main);
}

	.footer-bottom {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
	}

	.footer-bottom .social-media {
		order: 1;
	}

	.footer-bottom .copyright {
		order: 2;
	}

	.footer-bottom .seal-logos-container {
		order: 3;
	}

	.copyright {
		margin-bottom: 0.5rem;
		margin-top: 1rem;
		text-align: center;
}

	.copyright .footer-text {
		font-size: 12px !important;
	}

	.social-media {
		margin-bottom: 0.5rem;
		margin-top: 0.5rem;
}

	.seal-logos-container {
		margin-top: 1rem;
		margin-bottom: 1.25rem;
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
}

	.footer-logo {
		height: 30px;
		width: auto;
}

	@container (min-width: 600px) {
		.footer-link {
				font-size: 0.875rem;
				line-height: 1.25rem;
		}

		.footer-text {
				font-size: 0.875rem;
				line-height: 1.25rem;
		}

		.footer-bottom {
			flex-direction: row;
			justify-content: space-between;
			align-items: center;
		}

		.copyright {
				margin-bottom: 0px;
				margin-top: 0px;
				text-align: left;
		}

		.social-media {
				margin-top: 0px;
		}

		.seal-logos-container {
				justify-content: center;
				gap: 1.5rem;
			width: auto;
			flex: 1;
		}

		.footer-logo {
				height: 35px;
				width: auto;
		}

		.footer-bottom .social-media,
		.footer-bottom .copyright,
		.footer-bottom .seal-logos-container {
			order: initial;
		}

		.copyright {
			flex: 0 0 auto;
		}
	}

	@media (min-width: 1024px) {
		.footer-container {
				margin-bottom: 2rem;
		}
	}
</style>
